import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { api } from '../utils/apiInterceptor';
// Removed unused import
import { secureStorageGet } from '../utils/encryption';
import './GeneratePage.scss';

interface GenerationSuggestions {
  topics: string[];
  difficulties: Array<{
    value: string;
    label: string;
    description: string;
    duration: string;
  }>;
  focusAreas: string[];
}

export const GeneratePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscriptionStatus, canGenerateCourse, refreshSubscriptionData } =
    useSubscription();
  const [loading, setLoading] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const [suggestions, setSuggestions] = useState<GenerationSuggestions | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [currentMessage, setCurrentMessage] = useState('');
  const [messageIndex, setMessageIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [failureDetails, setFailureDetails] = useState<any>(null);

  // Note: Removed automatic redirect - let backend handle subscription checks
  // Users should only be redirected if they actually get a 403 from the generate API

  // Dynamic loading messages that cycle through
  const loadingMessages = [
    'Analyzing your learning goals...',
    'Designing the perfect curriculum structure...',
    'Crafting lessons tailored to your level...',
    'Adding real-world examples and case studies...',
    'Incorporating the latest industry insights...',
    'Polishing content for maximum engagement...',
    'Preparing your personalized learning journey...',
    'Integrating interactive elements...',
    'Making learning fun and memorable...',
    'Almost ready to blow your mind...',
    'Generating quiz questions that matter...',
    'Adding that special Odyssey magic...',
    'Curating relevant video content...',
    'Optimizing difficulty progression...',
    'Adding personality to your course...',
    'Fine-tuning every detail...',
    'Making complex topics simple...',
    'Creating an unforgettable experience...',
    'Putting the finishing touches...',
    'Your course is almost ready!',
    'This can take up to a minute...',
    'Good things take time...',
    'Brewing something special...',
    'Quality over speed...',
    'Almost there...',
  ];

  // State to track if we're using real progress (disable cycling messages)
  const [usingRealProgress, setUsingRealProgress] = useState(false);

  // Cycle through loading messages (only if not using real progress)
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isGenerating && !usingRealProgress) {
      // Start with first message immediately
      setCurrentMessage(loadingMessages[0]);
      setMessageIndex(0);

      // Change message every 3-4 seconds with some randomness
      intervalId = setInterval(
        () => {
          setMessageIndex(prevIndex => {
            const nextIndex = (prevIndex + 1) % loadingMessages.length;
            const newMessage = loadingMessages[nextIndex];
            console.log('Changing generic message to:', newMessage);
            setCurrentMessage(newMessage);
            return nextIndex;
          });
        },
        3000 + Math.random() * 1000
      ); // 3-4 seconds
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isGenerating, loadingMessages, usingRealProgress]);

  const [formData, setFormData] = useState({
    topic: '',
    difficulty: 'basic' as 'basic' | 'intermediate' | 'advanced',
    focusAreas: [] as string[],
  });

  useEffect(() => {
    // Load suggestions
    api
      .get('/generate/suggestions')
      .then(response => {
        console.log('Full suggestions response:', response);
        console.log('Response data:', response.data);
        console.log('Difficulties found:', response.data?.difficulties || (response as any)?.difficulties);
        setSuggestions(response.data || response);
      })
      .catch(err => {
        console.error('Failed to load suggestions:', err);
        // Set default suggestions if API fails
        setSuggestions({
          topics: [
            'Machine Learning Fundamentals',
            'Web Development with React',
            'Data Science Basics',
            'Python Programming',
            'Digital Marketing'
          ],
          difficulties: [
            {
              value: 'basic',
              label: 'Basic',
              description: 'Perfect for beginners with no prior experience',
              duration: '2-4 hours'
            },
            {
              value: 'intermediate',
              label: 'Intermediate',
              description: 'For those with some foundational knowledge',
              duration: '4-6 hours'
            },
            {
              value: 'advanced',
              label: 'Advanced',
              description: 'Deep dive for experienced learners',
              duration: '6-8 hours'
            }
          ],
          focusAreas: [
            'Practical Applications',
            'Theory & Concepts',
            'Hands-on Projects',
            'Case Studies',
            'Best Practices',
            'Industry Insights'
          ]
        });
      })
      .finally(() => {
        setSuggestionsLoading(false);
      });
  }, []);


  // Real-time progress tracking with Server-Sent Events
  const connectToProgressStream = async () => {
    console.log('🔄 Using enhanced polling for real-time progress...');

    try {
      // Enable real progress tracking (disable cycling messages)
      setUsingRealProgress(true);
      setCurrentMessage('Starting course generation...');

      // Use the enhanced fallback with better progress tracking
      await fallbackWithBetterProgress();
    } catch (error) {
      console.error('Enhanced progress failed, using basic fallback:', error);
      // If enhanced fails, disable real progress and use old method
      setUsingRealProgress(false);
      await fallbackWithoutProgress();
    }
  };

  // Enhanced fallback with better progress tracking
  const fallbackWithBetterProgress = async () => {
    try {
      console.log(
        '📱 Using enhanced fallback API call with better progress...'
      );

      console.log('📡 Making API call to /generate/course (enhanced)');
      // Make direct request to bypass proxy issues
      const token = secureStorageGet('token');
      const response = await fetch(
        'http://localhost:3001/api/generate/course',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();
      console.log('📡 Enhanced API response received:', data);

      // Check if upgrade is required (free user trying to generate)
      if (response.status === 403 && data.upgradeRequired) {
        console.log('🔒 Upgrade required, redirecting to upgrade page');
        setIsGenerating(false);
        setLoading(false);
        navigate('/upgrade-plan');
        return;
      }

      // Check if response is not ok
      if (!response.ok) {
        throw new Error(
          data.error || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      setCurrentMessage('Course generation started! Setting up workspace...');

      // Poll for course completion with enhanced progress tracking
      if (data.course?.id) {
        console.log(
          '📊 Starting enhanced polling for course generation status...'
        );
        await pollForCourseCompletionEnhanced(data.course.id, token);
      } else {
        console.error('No course ID in response:', data);
        throw new Error('No course ID received from server');
      }
    } catch (error: any) {
      console.error('💥 Enhanced API call failed:', error);
      throw error; // Re-throw to be caught by handleSubmit
    }
  };

  // Enhanced polling with faster updates and better messages
  const pollForCourseCompletionEnhanced = async (
    courseId: string,
    token: string | null
  ) => {
    const maxAttempts = 60; // 6 minutes max (6 second intervals)
    let attempts = 0;
    let lastStep = '';

    while (attempts < maxAttempts) {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/generate/course/${courseId}/status`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 429) {
          console.log('⏳ Rate limited, waiting longer before retry...');
          await new Promise(resolve => setTimeout(resolve, 8000)); // Wait 8 seconds
          attempts++;
          continue;
        }

        const data = await response.json();
        console.log(`📊 Enhanced poll attempt ${attempts + 1}:`, data);

        // Update message based on generation step with more detail
        if (data.generationStep && data.generationStep !== lastStep) {
          lastStep = data.generationStep;

          const stepMessages: Record<string, string> = {
            initializing: 'Initializing your personalized course...',
            generating_outline: 'Crafting the perfect course structure...',
            generating_lessons: 'Creating engaging lesson content...',
            generating_assessments:
              'Building interactive quizzes and assessments...',
            finalizing: 'Adding final touches and polish...',
            completed: 'Course generation complete! 🎉',
            failed: 'Course generation encountered an error',
            failure_handler_failed: 'Critical system error occurred',
          };

          const message =
            stepMessages[data.generationStep] ||
            `Processing: ${data.generationStep}`;
          console.log(`🎯 Setting progress message: ${message}`);
          setCurrentMessage(message);

          // Add some excitement for major milestones
          if (data.generationStep === 'generating_lessons') {
            setTimeout(() => {
              console.log(
                '🎯 Setting milestone message: Adding real-world examples...'
              );
              setCurrentMessage(
                'Adding real-world examples and case studies...'
              );
            }, 2000);
          }
          if (data.generationStep === 'generating_assessments') {
            setTimeout(() => {
              console.log(
                '🎯 Setting milestone message: Creating practice questions...'
              );
              setCurrentMessage('Creating personalized practice questions...');
            }, 2000);
          }
        }

        // Show lesson progress if available
        if (data.progress && data.progress.completedLessons !== undefined) {
          const { completedLessons, totalLessons } = data.progress;
          if (totalLessons > 0) {
            setCurrentMessage(
              `Generating lessons... (${completedLessons}/${totalLessons} complete)`
            );
          }
        }

        // Navigate after outline is generated (Step 2 complete)
        if (
          data.status === 'published' ||
          data.generationStep === 'generating_lessons' ||
          data.generationStep === 'generating_assessments' ||
          data.generationStep === 'finalizing' ||
          data.generationStep === 'completed' ||
          data.outline
        ) {
          console.log('✅ Course ready for viewing, navigating to course page');
          setCurrentMessage('🎉 Your course is ready! Preparing to launch...');
          setSuccess(true);
          setIsGenerating(false);

          setTimeout(() => {
            navigate(`/courses/${courseId}`);
          }, 1500);
          return;
        }

        // Check for failure
        if (data.status === 'failed') {
          setCurrentMessage('Course generation failed');

          // Store failure details for display
          setFailureDetails({
            error: data.metadata?.error,
            partialProgress: data.partialProgress,
            generationStep: data.generationStep,
            failedAt: data.failedAt,
          });

          // Create detailed failure message based on step function failure data
          let failureMessage = 'Course generation failed';

          if (data.metadata?.error) {
            const errorInfo = data.metadata.error;
            failureMessage = `${errorInfo.message || 'Course generation failed'}`;

            // Add retry information if available
            if (data.partialProgress?.completionRate > 0) {
              failureMessage += ` (${Math.round(data.partialProgress.completionRate)}% completed before failure)`;
            }

            // Add retry suggestion for retryable errors
            if (errorInfo.retryable) {
              failureMessage +=
                '. This error may be temporary - please try generating the course again.';
            } else {
              failureMessage +=
                '. Please check your course topic and try again, or contact support if the issue persists.';
            }
          }

          throw new Error(failureMessage);
        }

        // Wait before next poll (4 seconds for more responsive updates)
        await new Promise(resolve => setTimeout(resolve, 4000));
        attempts++;
      } catch (error) {
        console.error('❌ Error polling for course status:', error);
        if (attempts >= maxAttempts - 1) {
          throw new Error(
            'Course generation timed out. Please check your courses page or try again.'
          );
        }
      }
    }

    // If we get here, we've exceeded max attempts
    throw new Error(
      'Course generation is taking longer than expected. Please check your courses page in a few minutes.'
    );
  };

  // Fallback without progress simulation - messages will cycle automatically
  const fallbackWithoutProgress = async () => {
    try {
      console.log('📱 Using fallback API call...');

      console.log('📡 Making API call to /generate/course (fallback)');
      // Make direct request to bypass proxy issues
      const token = secureStorageGet('token');
      const response = await fetch(
        'http://localhost:3001/api/generate/course',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();
      console.log('📡 Fallback API response received:', data);

      // Check if upgrade is required (free user trying to generate)
      if (response.status === 403 && data.upgradeRequired) {
        console.log('🔒 Upgrade required, redirecting to upgrade page');
        setIsGenerating(false);
        setLoading(false);
        navigate('/upgrade-plan');
        return;
      }

      // Check if response is not ok
      if (!response.ok) {
        throw new Error(
          data.error || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      setCurrentMessage(
        'Course generation started! Creating course structure...'
      );

      // Poll for course completion
      if (data.course?.id) {
        console.log('📊 Starting to poll for course generation status...');
        await pollForCourseCompletion(data.course.id, token);
      } else {
        console.error('No course ID in fallback response:', data);
        throw new Error('No course ID received from server');
      }
    } catch (error: any) {
      console.error('💥 Fallback API call failed:', error);
      throw error; // Re-throw to be caught by handleSubmit
    }
  };

  // Poll for course generation completion
  const pollForCourseCompletion = async (
    courseId: string,
    token: string | null
  ) => {
    const maxAttempts = 40; // 4 minutes max (6 second intervals)
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/generate/course/${courseId}/status`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 429) {
          console.log('⏳ Rate limited, waiting longer before retry...');
          await new Promise(resolve => setTimeout(resolve, 10000)); // Wait 10 seconds
          attempts++;
          continue;
        }

        const data = await response.json();
        console.log(`📊 Poll attempt ${attempts + 1}:`, data);

        // Update message based on generation step
        if (data.generationStep) {
          const stepMessages: Record<string, string> = {
            initializing: 'Initializing course generation...',
            generating_outline: 'Creating course outline...',
            generating_lessons: 'Generating lesson content...',
            generating_assessments: 'Creating quizzes and assessments...',
            finalizing: 'Finalizing course content...',
            completed: 'Course generation complete!',
            failed: 'Course generation failed',
            failure_handler_failed: 'Critical error occurred',
          };
          setCurrentMessage(
            stepMessages[data.generationStep] ||
              `Processing: ${data.generationStep}`
          );
        }

        // Navigate after outline is generated (Step 2 complete)
        if (
          data.status === 'published' ||
          data.generationStep === 'generating_lessons' ||
          data.generationStep === 'generating_assessments' ||
          data.generationStep === 'finalizing' ||
          data.generationStep === 'completed' ||
          data.outline
        ) {
          console.log('✅ Course outline complete, navigating to course page');
          setSuccess(true);
          setIsGenerating(false);
          setTimeout(() => {
            navigate(`/courses/${courseId}`);
          }, 1000);
          return;
        }

        // Check for failure
        if (data.status === 'failed') {
          setCurrentMessage('Course generation failed');

          // Store failure details for display
          setFailureDetails({
            error: data.metadata?.error,
            partialProgress: data.partialProgress,
            generationStep: data.generationStep,
            failedAt: data.failedAt,
          });

          // Create detailed failure message based on step function failure data
          let failureMessage = 'Course generation failed';

          if (data.metadata?.error) {
            const errorInfo = data.metadata.error;
            failureMessage = `${errorInfo.message || 'Course generation failed'}`;

            // Add retry information if available
            if (data.partialProgress?.completionRate > 0) {
              failureMessage += ` (${Math.round(data.partialProgress.completionRate)}% completed before failure)`;
            }

            // Add retry suggestion for retryable errors
            if (errorInfo.retryable) {
              failureMessage +=
                '. This error may be temporary - please try generating the course again.';
            } else {
              failureMessage +=
                '. Please check your course topic and try again, or contact support if the issue persists.';
            }
          }

          throw new Error(failureMessage);
        }

        // Wait before next poll (6 seconds to avoid rate limiting)
        await new Promise(resolve => setTimeout(resolve, 6000));
        attempts++;
      } catch (error) {
        console.error('❌ Error polling for course status:', error);
        if (attempts >= maxAttempts - 1) {
          throw new Error(
            'Course generation timed out. Please check your courses page.'
          );
        }
      }
    }

    // If we get here, we've exceeded max attempts
    throw new Error(
      'Course generation is taking longer than expected. Please check your courses page.'
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🚀 Starting course generation...', formData);

    setError(null);
    setLoading(true);
    setIsGenerating(true);
    setCurrentMessage(loadingMessages[0]);

    try {
      console.log('🔄 Connecting to progress stream...');
      await connectToProgressStream();
      console.log('✅ Course generation completed successfully');
    } catch (err: any) {
      console.error('Course generation failed:', err);

      // Check if it's a subscription limit error
      if (err.response?.status === 403) {
        const errorData = err.response.data;
        if (errorData.upgradeRequired) {
          // Redirect to upgrade page for subscription limits
          navigate('/upgrade-plan');
          return;
        }
      }

      setError(err.message || 'Failed to generate course');
      setCurrentMessage('');
      setIsGenerating(false);
      setUsingRealProgress(false); // Reset real progress tracking
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const handleFocusAreaToggle = (area: string) => {
    setFormData(prev => ({
      ...prev,
      focusAreas: prev.focusAreas?.includes(area)
        ? prev.focusAreas.filter(a => a !== area)
        : [...(prev.focusAreas || []), area],
    }));
  };

  const handleRetry = () => {
    setError(null);
    setFailureDetails(null);
    setSuccess(false);
  };

  // Remove the subscription check - show form to all users

  if (error && !loading) {
    return (
      <div className="generate-page">
        <div className="error-container">
          <div className="error-card">
            <div className="error-content">
              <div className="error-icon">
                <div className="error-symbol">!</div>
              </div>
              <h1 className="error-title">Course Generation Failed</h1>
              <p className="error-description">{error}</p>

              {failureDetails && (
                <div className="failure-details">
                  {failureDetails.generationStep && (
                    <p className="failure-step">
                      <strong>Failed during:</strong>{' '}
                      {failureDetails.generationStep.replace(/_/g, ' ')}
                    </p>
                  )}

                  {failureDetails.partialProgress &&
                    failureDetails.partialProgress.completionRate > 0 && (
                      <div className="partial-progress">
                        <p>
                          <strong>Progress before failure:</strong>
                        </p>
                        <div className="progress-stats">
                          <span>
                            {failureDetails.partialProgress.completedLessons} of{' '}
                            {failureDetails.partialProgress.totalLessons}{' '}
                            lessons completed
                          </span>
                          <div className="progress-bar">
                            <div
                              className="progress-fill"
                              style={{
                                width: `${failureDetails.partialProgress.completionRate}%`,
                              }}
                            />
                          </div>
                          <span>
                            {Math.round(
                              failureDetails.partialProgress.completionRate
                            )}
                            % complete
                          </span>
                        </div>
                      </div>
                    )}

                  {failureDetails.error?.retryable && (
                    <p className="retry-suggestion">
                      💡 This appears to be a temporary issue. Please try again.
                    </p>
                  )}
                </div>
              )}

              <div className="error-actions">
                <button
                  className="error-button retry-button"
                  onClick={handleRetry}
                >
                  Try Again
                </button>
                <button
                  className="error-button secondary"
                  onClick={() => navigate('/')}
                >
                  Go Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="generate-page">
        <div className="success-container">
          <div className="success-card">
            <div className="success-content">
              <div className="success-icon">
                <div className="success-checkmark">✓</div>
              </div>
              <h1 className="success-title">Course Generated Successfully!</h1>
              <p className="success-description">
                Your course has been created and is ready to explore.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="generate-page">
      <div className="container">
        <div className="generate-page-header">
          <h1>Generate Course</h1>
          <p>Create a personalized course tailored to your learning goals</p>
        </div>


        <div className="form-card">
          {error && (
            <div className="error-alert">
              <p className="error-text">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="form-container">
            {/* Topic */}
            <div className="form-group">
              <label className="form-label">
                Course Topic <span className="required">*</span>
              </label>
              <input
                type="text"
                value={formData.topic}
                onChange={e =>
                  setFormData(prev => ({ ...prev, topic: e.target.value }))
                }
                placeholder="e.g., Machine Learning Fundamentals"
                required
                disabled={loading}
                className="form-input"
              />

              <div className="topic-tips">
                <p className="tips-content">
                  Be as specific or general as you'd like.
                </p>
                <div className="tips-examples">
                  <p className="examples-list">
                    Physics, React for e-commerce, 19th century maritime
                    knot-tying for beginners
                  </p>
                </div>
              </div>

              {suggestionsLoading ? (
                <div className="suggestions-container shimmer-suggestions">
                  <p className="suggestions-label">Popular topics:</p>
                  <div className="suggestions-grid">
                    {[1, 2, 3, 4, 5].map(i => (
                      <div key={i} className="shimmer-suggestion"></div>
                    ))}
                  </div>
                </div>
              ) : (
                suggestions && (
                  <div className="suggestions-container">
                    <p className="suggestions-label">Popular topics:</p>
                    <div className="suggestions-grid">
                      {suggestions.topics.slice(0, 5).map(topic => (
                        <button
                          key={topic}
                          type="button"
                          onClick={() =>
                            setFormData(prev => ({ ...prev, topic }))
                          }
                          className="suggestion-button"
                          disabled={loading}
                        >
                          {topic}
                        </button>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Difficulty */}
            <div className="form-group">
              <label className="form-label">
                Difficulty Level <span className="required">*</span>
              </label>
              {suggestionsLoading ? (
                <div className="difficulty-grid shimmer-difficulty">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="shimmer-difficulty-option">
                      <div className="shimmer-title"></div>
                      <div className="shimmer-description"></div>
                      <div className="shimmer-duration"></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="difficulty-grid">
                  {suggestions?.difficulties ? suggestions.difficulties.map(diff => (
                    <div
                      key={diff.value}
                      className={`difficulty-option ${
                        formData.difficulty === diff.value ? 'selected' : ''
                      }`}
                      onClick={() =>
                        setFormData(prev => ({
                          ...prev,
                          difficulty: diff.value as any,
                        }))
                      }
                    >
                      <div className="option-header">
                        <input
                          type="radio"
                          value={diff.value}
                          checked={formData.difficulty === diff.value}
                          onChange={() =>
                            setFormData(prev => ({
                              ...prev,
                              difficulty: diff.value as any,
                            }))
                          }
                          className="option-radio"
                          disabled={loading}
                        />
                        <span className="option-title">{diff.label}</span>
                      </div>
                      <p className="option-description">{diff.description}</p>
                      <p className="option-duration">{diff.duration}</p>
                    </div>
                  )) : null}
                </div>
              )}
            </div>

            {/* Focus Areas */}
            <div className="form-group">
              <label className="form-label">Focus Areas (Optional)</label>
              {suggestionsLoading ? (
                <div className="focus-areas-grid shimmer-focus-areas">
                  {[1, 2, 3, 4, 5, 6].map(i => (
                    <div key={i} className="shimmer-focus-option"></div>
                  ))}
                </div>
              ) : (
                <div className="focus-areas-grid">
                  {suggestions?.focusAreas.map(area => (
                    <div
                      key={area}
                      className={`focus-area-option ${
                        formData.focusAreas?.includes(area) ? 'selected' : ''
                      }`}
                      onClick={() => handleFocusAreaToggle(area)}
                    >
                      <div className="focus-area-content">
                        <input
                          type="checkbox"
                          checked={formData.focusAreas?.includes(area) || false}
                          onChange={() => handleFocusAreaToggle(area)}
                          className="focus-area-checkbox"
                          disabled={loading}
                        />
                        <span className="focus-area-label">{area}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Course options removed - not yet supported */}

            {/* Submit Button */}
            <div className="form-actions">
              <button
                type="submit"
                disabled={loading || !formData.topic}
                className="submit-button"
              >
                {loading ? (
                  <div className="generate-loading-content">
                    <div className="generate-loading-spinner"></div>
                    <div className="generate-loading-message">
                      <span className="generate-message-text">
                        {currentMessage}
                      </span>
                    </div>
                  </div>
                ) : (
                  'Generate Course'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
